import * as THREE from 'three';

export function retargetClipToRig(
  srcRoot: THREE.Object3D,          // the emote GLB's own scene (its skeleton)
  clip: THREE.AnimationClip,
  dstRestWorldQ: Map<string, THREE.Quaternion>, // avatar rest world quats (all nodes)
  dstBoneOrder: { name: string; parent: string }[], // avatar bones, parents first
): THREE.AnimationClip {
  // Source rest pose (before the mixer touches it)
  srcRoot.updateMatrixWorld(true);
  const srcBones = new Map<string, THREE.Object3D>();
  srcRoot.traverse((o) => { if (o.name) srcBones.set(o.name, o); });
  const srcRestWorldQ = new Map<string, THREE.Quaternion>();
  const _wq = new THREE.Quaternion();
  for (const [n, b] of srcBones) { b.getWorldQuaternion(_wq); srcRestWorldQ.set(n, _wq.clone()); }

  const bones = dstBoneOrder.filter((b) => srcBones.has(b.name) && srcRestWorldQ.has(b.name) && dstRestWorldQ.has(b.name));
  if (bones.length === 0) return clip;

  const fps = 30;
  const N = Math.max(2, Math.round(clip.duration * fps) + 1);
  const times = new Float32Array(N);
  const out = new Map(bones.map((b) => [b.name, new Float32Array(N * 4)]));

  const mixer = new THREE.AnimationMixer(srcRoot);
  const action = mixer.clipAction(clip);
  action.setLoop(THREE.LoopRepeat, Infinity);
  action.play();

  const D = new THREE.Quaternion(), Wt = new THREE.Quaternion(), ql = new THREE.Quaternion(), inv = new THREE.Quaternion();
  const prev = new Map<string, THREE.Quaternion>();
  for (let f = 0; f < N; f++) {
    const t = Math.min((f / fps), clip.duration - 1e-4);
    times[f] = t;
    mixer.setTime(t);
    srcRoot.updateMatrixWorld(true);
    const worldQ = new Map<string, THREE.Quaternion>();
    for (const { name, parent } of bones) {
      srcBones.get(name)!.getWorldQuaternion(_wq);
      D.copy(_wq).multiply(inv.copy(srcRestWorldQ.get(name)!).invert());
      Wt.copy(D).multiply(dstRestWorldQ.get(name)!);
      const pW = worldQ.get(parent) || dstRestWorldQ.get(parent) || new THREE.Quaternion();
      ql.copy(pW).invert().multiply(Wt);
      const pv = prev.get(name);
      if (pv && ql.x * pv.x + ql.y * pv.y + ql.z * pv.z + ql.w * pv.w < 0) ql.set(-ql.x, -ql.y, -ql.z, -ql.w);
      prev.set(name, ql.clone());
      out.get(name)!.set([ql.x, ql.y, ql.z, ql.w], f * 4);
      worldQ.set(name, pW.clone().multiply(ql));
    }
  }
  mixer.stopAllAction();

  const tracks = bones.map(({ name }) => new THREE.QuaternionKeyframeTrack(`${name}.quaternion`, times as any, out.get(name)! as any));
  const rc = new THREE.AnimationClip(clip.name, clip.duration, tracks);
  return rc;
}
